import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import zoomSdk from "@zoom/appssdk";
import { AIMS_AVAILABILITY_MESSAGE_TYPES, AIMS_NOT_AVAILABLE_MESSAGE, AIMS_STATUS, AVAILABILITY_TIMEOUT_ERROR, ROLES, SUMMARIZE_STATES, TRANSCRIPTION_STATES, ZOOM_RUNNING_CONTEXT } from "../constants";
import LogManager from "../utils/logManager";
import { fetchAimsAvailabilityApi } from "../utils/aimsAvailabilityUtils";
import { fetchRtmsSessionStatus, SESSION_STATUS } from "../utils/rtmsSessionStatusUtils";
import { configureZoomSdk } from "../utils/zoomSdkUtils";
import {
  clearSummariesCache,
  handleAimsAvailabilityResult,
  resolveRtmsStartedByOther,
  canAutoStartTranscription,
} from "../utils/meetingInitUtils";

// What the in-meeting UI starts from. `initialSession` is the RTMS session already running when
// the app opened; the flow UIs (Home / Hire) own session state from there.
const DEFAULT_MEETING = {
  meetingUuid: null,
  meetingId: null,
  role: null,
  alias: null,
  appContext: null,
  aimsStatus: AIMS_STATUS.AVAILABLE,
  aimsMessage: null,
  aimsNotAvailableMessage: AIMS_NOT_AVAILABLE_MESSAGE.DEFAULT,
  hasGermanyAttendee: false,
  leoInterviewer: false,
  autoStartMode: null,
  inviteeAliases: null,
  externalInvitees: null,
  isAutostarting: false,
  initialSession: {
    summarizeState: SUMMARIZE_STATES.DEFAULT,
    transcriptionState: TRANSCRIPTION_STATES.DEFAULT,
    isRtmsStartedByOther: false,
    rtmsRequesterName: null,
    rtmsStreamId: null,
    rtmsStartTime: null,
    meetingAgendaActive: false,
    meetingActionItemsActive: false,
  },
};

/**
 * Configure the Zoom SDK and fetch meeting/user context, availability and RTMS session status once
 * on mount (on localhost the SDK and backend are mocked — src/localtest/mocks). Returns the meeting,
 * or null while loading.
 * In the main client it redirects to /client instead (meeting stays null).
 *
 * @param {object} params
 * @param {function} params.setLastKnownContext - Reports the Zoom running context to the router.
 * @param {function} params.loadPreferences - Loads user preferences alongside availability.
 */
export const useInMeetingInitialization = ({ setLastKnownContext, loadPreferences }) => {
  const [meeting, setMeeting] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const m = { ...DEFAULT_MEETING, initialSession: { ...DEFAULT_MEETING.initialSession } };
    const session = m.initialSession;

    LogManager.metric("Home_PageView", 1);

    // Configure the Zoom SDK and read the running/meeting/user context. Returns
    // { isMainClient: true } when running in the main client (nothing more to do),
    // otherwise { isMainClient: false, meetingUUID, userContext }.
    const loadZoomContext = async () => {
      await configureZoomSdk();

      const runningContext = await zoomSdk.getRunningContext();
      LogManager.info("The runningContext is:", runningContext);

      console.log("Document Visibility State:", document.visibilityState);
      setLastKnownContext(runningContext.context);

      if (runningContext.context === ZOOM_RUNNING_CONTEXT.IN_MAIN_CLIENT) {
        LogManager.info("The app is running in main client now, show MainClientHome view.");
        return { isMainClient: true };
      }

      // The runningContext is inMeeting
      const meetingUUID = (await zoomSdk.getMeetingUUID()).meetingUUID;
      console.log("The meetingUUID is:" + meetingUUID);
      m.meetingUuid = meetingUUID;

      const userContext = await zoomSdk.getUserContext();
      LogManager.info("The userContext is:", userContext);

      return { isMainClient: false, meetingUUID, userContext };
    };

    // Fetch AIMS availability for hosts/coHosts and apply the results. Attendees
    // have no availability call. Returns the aimsAvailability response (or null).
    const loadHostAvailability = async (meetingUUID, userContext) => {
      if (!["host", "coHost"].includes(userContext.role)) {
        LogManager.info("The app is running inMeeting and user is attendee.");
        m.role = ROLES.ATTENDEE;
        loadPreferences();
        return null;
      }

      LogManager.info("The app is running inMeeting and user is host or coHost.");

      // Only host and coHost can call this api
      const meetingContext = await zoomSdk.getMeetingContext();
      LogManager.info("The meetingContext is: ", meetingContext);
      m.meetingId = meetingContext.meetingID;

      const [aimsAvailability] = await Promise.all([
        fetchAimsAvailabilityApi(
          meetingUUID,
          meetingContext.meetingID,
          meetingContext.meetingTopic,
          userContext.screenName
        ),
        loadPreferences(),
      ]);

      const availabilityResult = handleAimsAvailabilityResult(aimsAvailability);
      m.role = availabilityResult.role;
      m.alias = availabilityResult.alias;
      m.hasGermanyAttendee = availabilityResult.hasGermanyAttendee;
      m.aimsStatus = availabilityResult.aimsStatus;
      m.aimsMessage = availabilityResult.aimsMessage;
      m.leoInterviewer = availabilityResult.aimsMessage === AIMS_AVAILABILITY_MESSAGE_TYPES.HIRE_LEO;
      m.autoStartMode = availabilityResult.autoStartMode;
      m.inviteeAliases = availabilityResult.inviteeAliases;
      m.externalInvitees = availabilityResult.externalInvitees;
      if (availabilityResult.aimsNotAvailableMessage) {
        m.aimsNotAvailableMessage = availabilityResult.aimsNotAvailableMessage;
      }

      // Get app context for secure backend validation (only needed for organizers/alternative hosts)
      try {
        const appContextResponse = await zoomSdk.getAppContext();
        LogManager.metric("ZoomSDK_GetAppContext_Success", 1);
        // Log only that a context arrived, NEVER its value (Requirement 3.9) — see
        // the note in pages/Home.jsx for why.
        LogManager.info(
          "app context received: " +
            (appContextResponse?.context ? "present" : "absent")
        );

        if (!appContextResponse || !appContextResponse.context) {
          LogManager.error("getAppContext() returned invalid response - cannot proceed with RTMS operations");
          m.aimsStatus = AIMS_STATUS.EXCEPTION;
        } else {
          m.appContext = appContextResponse.context;
        }
      } catch (err) {
        LogManager.error("Failed to get app context from Zoom SDK:", err);
        LogManager.metric("ZoomSDK_GetAppContext_Failure", 1);
        m.aimsStatus = AIMS_STATUS.EXCEPTION;
      }

      LogManager.info("Finish the initialization for host or coHost.");
      return aimsAvailability;
    };

    // Fetch the RTMS session status (all user types) and apply the results.
    // Returns whether there is an active session.
    const loadRtmsSessionStatus = async (userContext, aimsAvailability, meetingUUID) => {
      const sessionStatus = await fetchRtmsSessionStatus(meetingUUID);
      LogManager.info("RTMS status is", sessionStatus);

      const hasActiveSession = sessionStatus.status === SESSION_STATUS.ACTIVE;

      if (hasActiveSession) {
        session.summarizeState = SUMMARIZE_STATES.SUMMARIZING;
        session.transcriptionState = TRANSCRIPTION_STATES.TRANSCRIBING;
        session.rtmsStartTime = sessionStatus.rtmsStartTime;

        if (sessionStatus.rtmsStreamId) {
          session.rtmsStreamId = sessionStatus.rtmsStreamId;
          LogManager.info("Saved rtmsStreamId on initialization:", sessionStatus.rtmsStreamId);
        }

        session.meetingAgendaActive = sessionStatus.meetingAgendaActive;
        session.meetingActionItemsActive = sessionStatus.meetingActionItemsActive;

        // Determine who started the session
        const { isStartedByOther, requesterName } = resolveRtmsStartedByOther({
          userContext,
          aimsAvailability,
          sessionStatus,
        });
        session.isRtmsStartedByOther = isStartedByOther;
        if (isStartedByOther) {
          session.rtmsRequesterName = requesterName;
        }
      } else {
        LogManager.info("No RTMS session found");
      }

      return hasActiveSession;
    };

    // Transcription auto-start (single-stream needs no active session; per-host multi always applies)
    const autoStartTranscription = (aimsAvailability, hasActiveSession) => {
      if (canAutoStartTranscription({ aimsAvailability, hasActiveSession })) {
        LogManager.info(
          `Autostart conditions met for transcription: transcript available, enableAutoStart is true, ` +
            `autoStartMode is ${aimsAvailability.autoStartMode}, hasActiveSession is ${hasActiveSession}`
        );
        m.isAutostarting = true;
      }
    };

    // Orchestrate the meeting initialization sequence, collecting results into `m`.
    const runInitialization = async () => {
      clearSummariesCache();

      try {
        const zoomContext = await loadZoomContext();
        if (zoomContext.isMainClient) {
          navigate("/client");
          return;
        }

        const { meetingUUID, userContext } = zoomContext;
        const aimsAvailability = await loadHostAvailability(meetingUUID, userContext);
        const hasActiveSession = await loadRtmsSessionStatus(userContext, aimsAvailability, meetingUUID);
        autoStartTranscription(aimsAvailability, hasActiveSession);
      } catch (error) {
        LogManager.error("Error fetching context or call AimsAvailability/RTMS Status API:", error);
        m.aimsStatus = error.message === AVAILABILITY_TIMEOUT_ERROR ? AIMS_STATUS.TIMEOUT : AIMS_STATUS.EXCEPTION;
      }
      setMeeting(m);
    };

    runInitialization();
    // Run once on mount.
  }, []);

  return meeting;
};
